/**
 * Texts for Account settings -> Profile page (namespace "updateProfile", key prefix "page.").
 * German and Croatian are typed as `typeof pageEn`, so the compiler guarantees every key exists in all languages.
 */
export const pageEn = {
    profile: "Profile",
    bio: "Bio",
    writeHere: "Write here...",
    characters: "Characters: {{count}}/{{max}}",
    editImage: "Edit image",
    viewProfile: "View profile",
    saveChange: "Save change",
    saveChanges: "Save changes",
    saving: "Saving...",
    cancel: "Cancel",
    add: "Add",
    update: "Update",
    edit: "Edit",
    remove: "Remove",
    unit: "Unit",
    completeFirst: "Please complete \"{{field}}\" first, it is required to save your profile.",
    loadOptionsFailed: "Could not load the dropdown options. Please refresh the page.",
    saveFailed: "Could not save your changes. Please try again.",

    personalDetails: "Personal details",
    firstName: "First name",
    firstNamePlaceholder: "Enter first name",
    lastName: "Last name",
    lastNamePlaceholder: "Enter last name",
    gender: "Gender",
    genderPlaceholder: "Select your gender",
    genders: {
        MALE: "Male",
        FEMALE: "Female",
        OTHER: "Other",
    },
    nickname: "Nickname",
    nicknamePlaceholder: "Enter nickname",
    dateOfBirth: "Date of birth",
    organizationName: "Organization name",
    enterOrganizationName: "Enter organization name",
    organization: "Organization",

    locationDetails: "Location details",
    nationality: "Nationality",
    nationalityPlaceholder: "Select nationality",
    residenceCountry: "Residence country",
    residenceCountryPlaceholder: "Select country",
    location: "Location",
    selectLocation: "Select location",

    fighterBasic: "Fighter profile basic details",
    disciplines: "Competing in disciplines",
    disciplinesPlaceholder: "Select competing in disciplines",
    weightClasses: "Weight classes",
    weightClassesPlaceholder: "Select weight classes",
    height: "Height",
    weight: "Weight",
    reach: "Reach",
    units: {
        cm: "cm",
        ft: "ft",
        inch: "inch",
        kgs: "kg",
        lbs: "lbs",
    },
    stance: "Stance",
    stancePlaceholder: "Select stance",
    stances: {
        Orthodox: "Orthodox",
        Southpaw: "Southpaw",
        Switch: "Switch",
    },
    titleHolder: "Title holder",
    addAchievement: "Add achievement",
    addAchievementBtn: "Add achievement",
    achievementN: "Achievement #{{n}}",
    enterAchievement: "Enter achievement",

    fighterAdditional: "Fighter profile additional details",
    team: "Team",
    enterTeam: "Enter team",
    gym: "Gym",
    enterGym: "Enter gym",
    fightStyle: "Fight style description",

    fightHistory: "Fight history",
    addFightHistory: "Add fight history",
    fightN: "Fight #{{n}}",
    opponentName: "Opponent name",
    enterOpponentName: "Enter opponent name",
    date: "Date",
    result: "Result",
    selectResult: "Select result",
    results: {
        Win: "Win",
        Loss: "Loss",
        Draw: "Draw",
    },
    round: "Round",
    method: "Method",
    selectMethod: "Select type of ending",
    endings: {
        KO: "KO",
        TKO: "TKO",
        SUBMISSION: "Submission",
        DECISION: "Decision",
        DRAW: "Draw",
        DQ: "DQ",
        NO_CONTEST: "No contest",
    },
    competitionLevel: "Competition level",
    selectLevel: "Select level",
    levels: {
        AMETEUR: "Amateur",
        SEMI_PRO: "Semi pro",
        PROFESSIONAL: "Professional",
    },
    addFight: "Add fight",
    updateFight: "Update fight",

    socialMediaLinks: "Social media links (optional)",
    addSocialMediaLink: "Add social media link",
    socialPlatform: "Social media platform",
    selectPlatform: "Select platform",
    profileLink: "Enter profile link",
    profileLinkPlaceholder: "Profile",
    profileLinkExample: "Example: https://facebook.com/username",

    youtubeLinks: "YouTube links",
    addYoutubeLinks: "Add YouTube links",
    youtubeUrl: "YouTube link",
    enterUrl: "Enter URL",
    shortTitle: "Short title",
    enterShortTitle: "Enter short title",
    shortDescription: "Short description",
    enterShortDescription: "Enter short description",

    sponsors: "Sponsors",
    addSponsors: "Add sponsors",
    uploadImage: "Upload profile image",
    uploadHint: "PNG, JPG (max 2MB)",
    sponsorName: "Sponsor name",
    enterSponsorName: "Enter sponsor name",
    sponsorDescription: "Sponsor description",
    companyWebsite: "Company website",
    addSponsor: "Add sponsor",
    addedOn: "Added on {{date}}",

    deleteAccount: "Delete account",
    deleteText:
        "Your account will be permanently deleted after one month. You can cancel the deletion before then. After deletion, your account and associated data can't be recovered.",
    deleteConfirmBody:
        "Your account will be scheduled for deletion and permanently removed after one month. You can cancel the deletion before then.",
    deleteScheduled: "Your account is scheduled for deletion.",
    deleteScheduledOn: "Your account will be permanently deleted on {{date}}.",
    deleteCancelled: "Account deletion cancelled.",
    deleteUnavailable: "Account deletion is not available yet. Please try again later.",
    cancelDeletion: "Cancel deletion",

    nav: {
        profile: "Profile",
        personalDetails: "Personal details",
        locationDetails: "Location details",
        fighterBasic: "Fighter profile basic details",
        fighterAdditional: "Fighter profile additional details",
        fightHistory: "Fight history",
        youtubeLinks: "YouTube links",
        sponsors: "Sponsors",
        linkAccount: "Link account",
        deleteAccount: "Delete account",
    },

    errors: {
        required: "This field is required",
        wholeNumber: "Enter a whole number",
        bioLength: "Bio must be 1000 characters or less",
        fixHighlighted: "Please fix the highlighted fields.",
        genderRequired: "Gender is required",
        dobRequired: "Date of birth is required",
        dobPast: "Date of birth must be in the past",
        maxLength: "Maximum {{max}} characters allowed",
        roundMin: "Round must be 1 or more",
        lastFight: "At least one fight must stay in your fight history",
        imageType: "Please choose an image file",
        imageSize: "Image must be {{max}} MB or smaller",
        imageUpload: "Image upload failed. Please try again.",
        invalidUrl: "URL must start with http:// or https://",
        logoType: "Only PNG or JPG images are allowed",
        logoSize: "Image must be 2 MB or smaller",
    },
};

export const pageDe: typeof pageEn = {
    profile: "Profil",
    bio: "Biografie",
    writeHere: "Hier schreiben...",
    characters: "Zeichen: {{count}}/{{max}}",
    editImage: "Bild bearbeiten",
    viewProfile: "Profil ansehen",
    saveChange: "Änderung speichern",
    saveChanges: "Änderungen speichern",
    saving: "Wird gespeichert...",
    cancel: "Abbrechen",
    add: "Hinzufügen",
    update: "Aktualisieren",
    edit: "Bearbeiten",
    remove: "Entfernen",
    unit: "Einheit",
    completeFirst: "Bitte füllen Sie zuerst \"{{field}}\" aus, es ist zum Speichern Ihres Profils erforderlich.",
    loadOptionsFailed: "Die Auswahloptionen konnten nicht geladen werden. Bitte laden Sie die Seite neu.",
    saveFailed: "Ihre Änderungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.",

    personalDetails: "Persönliche Angaben",
    firstName: "Vorname",
    firstNamePlaceholder: "Vorname eingeben",
    lastName: "Nachname",
    lastNamePlaceholder: "Nachname eingeben",
    gender: "Geschlecht",
    genderPlaceholder: "Geschlecht auswählen",
    genders: {
        MALE: "Männlich",
        FEMALE: "Weiblich",
        OTHER: "Divers",
    },
    nickname: "Spitzname",
    nicknamePlaceholder: "Spitznamen eingeben",
    dateOfBirth: "Geburtsdatum",
    organizationName: "Name der Organisation",
    enterOrganizationName: "Name der Organisation eingeben",
    organization: "Organisation",

    locationDetails: "Standortangaben",
    nationality: "Staatsangehörigkeit",
    nationalityPlaceholder: "Staatsangehörigkeit auswählen",
    residenceCountry: "Wohnsitzland",
    residenceCountryPlaceholder: "Land auswählen",
    location: "Ort",
    selectLocation: "Ort auswählen",

    fighterBasic: "Kämpferprofil – Grunddaten",
    disciplines: "Teilnahme an Disziplinen",
    disciplinesPlaceholder: "Disziplinen auswählen",
    weightClasses: "Gewichtsklassen",
    weightClassesPlaceholder: "Gewichtsklassen auswählen",
    height: "Größe",
    weight: "Gewicht",
    reach: "Reichweite",
    units: {
        cm: "cm",
        ft: "ft",
        inch: "Zoll",
        kgs: "kg",
        lbs: "lbs",
    },
    stance: "Kampfhaltung",
    stancePlaceholder: "Kampfhaltung auswählen",
    stances: {
        Orthodox: "Orthodox",
        Southpaw: "Linksausleger",
        Switch: "Wechselnd",
    },
    titleHolder: "Titelträger",
    addAchievement: "Erfolg hinzufügen",
    addAchievementBtn: "Erfolg hinzufügen",
    achievementN: "Erfolg Nr. {{n}}",
    enterAchievement: "Erfolg eingeben",

    fighterAdditional: "Kämpferprofil – Zusätzliche Angaben",
    team: "Team",
    enterTeam: "Team eingeben",
    gym: "Gym",
    enterGym: "Gym eingeben",
    fightStyle: "Beschreibung des Kampfstils",

    fightHistory: "Kampfhistorie",
    addFightHistory: "Kampfhistorie hinzufügen",
    fightN: "Kampf Nr. {{n}}",
    opponentName: "Name des Gegners",
    enterOpponentName: "Name des Gegners eingeben",
    date: "Datum",
    result: "Ergebnis",
    selectResult: "Ergebnis auswählen",
    results: {
        Win: "Sieg",
        Loss: "Niederlage",
        Draw: "Unentschieden",
    },
    round: "Runde",
    method: "Methode",
    selectMethod: "Art des Endes auswählen",
    endings: {
        KO: "K.o.",
        TKO: "TKO",
        SUBMISSION: "Aufgabe",
        DECISION: "Entscheidung",
        DRAW: "Unentschieden",
        DQ: "Disqualifikation",
        NO_CONTEST: "Kein Wettkampf",
    },
    competitionLevel: "Wettkampfniveau",
    selectLevel: "Niveau auswählen",
    levels: {
        AMETEUR: "Amateur",
        SEMI_PRO: "Semiprofi",
        PROFESSIONAL: "Profi",
    },
    addFight: "Kampf hinzufügen",
    updateFight: "Kampf aktualisieren",

    socialMediaLinks: "Social-Media-Links (optional)",
    addSocialMediaLink: "Social-Media-Link hinzufügen",
    socialPlatform: "Social-Media-Plattform",
    selectPlatform: "Plattform auswählen",
    profileLink: "Profillink eingeben",
    profileLinkPlaceholder: "Profil",
    profileLinkExample: "Beispiel: https://facebook.com/username",

    youtubeLinks: "YouTube-Links",
    addYoutubeLinks: "YouTube-Links hinzufügen",
    youtubeUrl: "YouTube-Link",
    enterUrl: "URL eingeben",
    shortTitle: "Kurztitel",
    enterShortTitle: "Kurztitel eingeben",
    shortDescription: "Kurzbeschreibung",
    enterShortDescription: "Kurzbeschreibung eingeben",

    sponsors: "Sponsoren",
    addSponsors: "Sponsoren hinzufügen",
    uploadImage: "Profilbild hochladen",
    uploadHint: "PNG, JPG (max. 2 MB)",
    sponsorName: "Name des Sponsors",
    enterSponsorName: "Name des Sponsors eingeben",
    sponsorDescription: "Beschreibung des Sponsors",
    companyWebsite: "Website des Unternehmens",
    addSponsor: "Sponsor hinzufügen",
    addedOn: "Hinzugefügt am {{date}}",

    deleteAccount: "Konto löschen",
    deleteText:
        "Ihr Konto wird nach einem Monat endgültig gelöscht. Sie können die Löschung bis dahin abbrechen. Nach der Löschung können Ihr Konto und die zugehörigen Daten nicht wiederhergestellt werden.",
    deleteConfirmBody:
        "Ihr Konto wird zur Löschung vorgemerkt und nach einem Monat endgültig entfernt. Sie können die Löschung bis dahin abbrechen.",
    deleteScheduled: "Ihr Konto wurde zur Löschung vorgemerkt.",
    deleteScheduledOn: "Ihr Konto wird am {{date}} endgültig gelöscht.",
    deleteCancelled: "Die Kontolöschung wurde abgebrochen.",
    deleteUnavailable: "Die Kontolöschung ist noch nicht verfügbar. Bitte versuchen Sie es später erneut.",
    cancelDeletion: "Löschung abbrechen",

    nav: {
        profile: "Profil",
        personalDetails: "Persönliche Angaben",
        locationDetails: "Standortangaben",
        fighterBasic: "Kämpferprofil – Grunddaten",
        fighterAdditional: "Kämpferprofil – Zusätzliche Angaben",
        fightHistory: "Kampfhistorie",
        youtubeLinks: "YouTube-Links",
        sponsors: "Sponsoren",
        linkAccount: "Konto verknüpfen",
        deleteAccount: "Konto löschen",
    },

    errors: {
        required: "Dieses Feld ist erforderlich",
        wholeNumber: "Geben Sie eine ganze Zahl ein",
        bioLength: "Die Biografie darf höchstens 1000 Zeichen lang sein",
        fixHighlighted: "Bitte korrigiere die markierten Felder.",
        genderRequired: "Geschlecht ist erforderlich",
        dobRequired: "Geburtsdatum ist erforderlich",
        dobPast: "Das Geburtsdatum muss in der Vergangenheit liegen",
        maxLength: "Maximal {{max}} Zeichen erlaubt",
        roundMin: "Die Runde muss mindestens 1 sein",
        lastFight: "Mindestens ein Kampf muss in Ihrer Kampfhistorie bleiben",
        imageType: "Bitte wählen Sie eine Bilddatei",
        imageSize: "Das Bild darf höchstens {{max}} MB groß sein",
        imageUpload: "Bild-Upload fehlgeschlagen. Bitte versuchen Sie es erneut.",
        invalidUrl: "Die URL muss mit http:// oder https:// beginnen",
        logoType: "Nur PNG- oder JPG-Bilder sind erlaubt",
        logoSize: "Das Bild darf höchstens 2 MB groß sein",
    },
};

export const pageHr: typeof pageEn = {
    profile: "Profil",
    bio: "Biografija",
    writeHere: "Pišite ovdje...",
    characters: "Znakova: {{count}}/{{max}}",
    editImage: "Uredi sliku",
    viewProfile: "Pogledaj profil",
    saveChange: "Spremi promjenu",
    saveChanges: "Spremi promjene",
    saving: "Spremanje...",
    cancel: "Odustani",
    add: "Dodaj",
    update: "Ažuriraj",
    edit: "Uredi",
    remove: "Ukloni",
    unit: "Jedinica",
    completeFirst: "Prvo ispunite \"{{field}}\", obavezno je za spremanje profila.",
    loadOptionsFailed: "Opcije za odabir nisu se mogle učitati. Osvježite stranicu.",
    saveFailed: "Promjene nisu spremljene. Pokušajte ponovno.",

    personalDetails: "Osobni podaci",
    firstName: "Ime",
    firstNamePlaceholder: "Unesite ime",
    lastName: "Prezime",
    lastNamePlaceholder: "Unesite prezime",
    gender: "Spol",
    genderPlaceholder: "Odaberite spol",
    genders: {
        MALE: "Muško",
        FEMALE: "Žensko",
        OTHER: "Drugo",
    },
    nickname: "Nadimak",
    nicknamePlaceholder: "Unesite nadimak",
    dateOfBirth: "Datum rođenja",
    organizationName: "Naziv organizacije",
    enterOrganizationName: "Unesite naziv organizacije",
    organization: "Organizacija",

    locationDetails: "Podaci o lokaciji",
    nationality: "Nacionalnost",
    nationalityPlaceholder: "Odaberite nacionalnost",
    residenceCountry: "Država prebivališta",
    residenceCountryPlaceholder: "Odaberite državu",
    location: "Lokacija",
    selectLocation: "Odaberite lokaciju",

    fighterBasic: "Profil borca – osnovni podaci",
    disciplines: "Natječe se u disciplinama",
    disciplinesPlaceholder: "Odaberite discipline",
    weightClasses: "Težinske kategorije",
    weightClassesPlaceholder: "Odaberite težinske kategorije",
    height: "Visina",
    weight: "Težina",
    reach: "Raspon ruku",
    units: {
        cm: "cm",
        ft: "ft",
        inch: "inč",
        kgs: "kg",
        lbs: "lbs",
    },
    stance: "Stav",
    stancePlaceholder: "Odaberite stav",
    stances: {
        Orthodox: "Ortodoksni",
        Southpaw: "Ljevoruki",
        Switch: "Promjenjivi",
    },
    titleHolder: "Nositelj naslova",
    addAchievement: "Dodaj postignuće",
    addAchievementBtn: "Dodaj postignuće",
    achievementN: "Postignuće br. {{n}}",
    enterAchievement: "Unesite postignuće",

    fighterAdditional: "Profil borca – dodatni podaci",
    team: "Tim",
    enterTeam: "Unesite tim",
    gym: "Klub",
    enterGym: "Unesite klub",
    fightStyle: "Opis borbenog stila",

    fightHistory: "Povijest borbi",
    addFightHistory: "Dodaj povijest borbi",
    fightN: "Borba br. {{n}}",
    opponentName: "Ime protivnika",
    enterOpponentName: "Unesite ime protivnika",
    date: "Datum",
    result: "Rezultat",
    selectResult: "Odaberite rezultat",
    results: {
        Win: "Pobjeda",
        Loss: "Poraz",
        Draw: "Neriješeno",
    },
    round: "Runda",
    method: "Način",
    selectMethod: "Odaberite vrstu završetka",
    endings: {
        KO: "KO",
        TKO: "TKO",
        SUBMISSION: "Predaja",
        DECISION: "Odluka sudaca",
        DRAW: "Neriješeno",
        DQ: "Diskvalifikacija",
        NO_CONTEST: "Bez rezultata",
    },
    competitionLevel: "Razina natjecanja",
    selectLevel: "Odaberite razinu",
    levels: {
        AMETEUR: "Amater",
        SEMI_PRO: "Poluprofesionalac",
        PROFESSIONAL: "Profesionalac",
    },
    addFight: "Dodaj borbu",
    updateFight: "Ažuriraj borbu",

    socialMediaLinks: "Poveznice na društvene mreže (neobavezno)",
    addSocialMediaLink: "Dodaj poveznicu na društvenu mrežu",
    socialPlatform: "Društvena mreža",
    selectPlatform: "Odaberite platformu",
    profileLink: "Unesite poveznicu na profil",
    profileLinkPlaceholder: "Profil",
    profileLinkExample: "Primjer: https://facebook.com/username",

    youtubeLinks: "YouTube poveznice",
    addYoutubeLinks: "Dodaj YouTube poveznice",
    youtubeUrl: "YouTube poveznica",
    enterUrl: "Unesite URL",
    shortTitle: "Kratki naslov",
    enterShortTitle: "Unesite kratki naslov",
    shortDescription: "Kratki opis",
    enterShortDescription: "Unesite kratki opis",

    sponsors: "Sponzori",
    addSponsors: "Dodaj sponzore",
    uploadImage: "Prenesi profilnu sliku",
    uploadHint: "PNG, JPG (maks. 2 MB)",
    sponsorName: "Naziv sponzora",
    enterSponsorName: "Unesite naziv sponzora",
    sponsorDescription: "Opis sponzora",
    companyWebsite: "Web stranica tvrtke",
    addSponsor: "Dodaj sponzora",
    addedOn: "Dodano {{date}}",

    deleteAccount: "Izbriši račun",
    deleteText:
        "Vaš račun bit će trajno izbrisan nakon mjesec dana. Brisanje možete otkazati prije toga. Nakon brisanja račun i povezani podaci ne mogu se vratiti.",
    deleteConfirmBody:
        "Vaš račun bit će zakazan za brisanje i trajno uklonjen nakon mjesec dana. Brisanje možete otkazati prije toga.",
    deleteScheduled: "Vaš račun je zakazan za brisanje.",
    deleteScheduledOn: "Vaš račun bit će trajno izbrisan {{date}}.",
    deleteCancelled: "Brisanje računa je otkazano.",
    deleteUnavailable: "Brisanje računa još nije dostupno. Pokušajte ponovno kasnije.",
    cancelDeletion: "Otkaži brisanje",

    nav: {
        profile: "Profil",
        personalDetails: "Osobni podaci",
        locationDetails: "Podaci o lokaciji",
        fighterBasic: "Profil borca – osnovni podaci",
        fighterAdditional: "Profil borca – dodatni podaci",
        fightHistory: "Povijest borbi",
        youtubeLinks: "YouTube poveznice",
        sponsors: "Sponzori",
        linkAccount: "Poveži račun",
        deleteAccount: "Izbriši račun",
    },

    errors: {
        required: "Ovo polje je obavezno",
        wholeNumber: "Unesite cijeli broj",
        bioLength: "Biografija može imati najviše 1000 znakova",
        fixHighlighted: "Ispravite označena polja.",
        genderRequired: "Spol je obavezan",
        dobRequired: "Datum rođenja je obavezan",
        dobPast: "Datum rođenja mora biti u prošlosti",
        maxLength: "Dopušteno je najviše {{max}} znakova",
        roundMin: "Runda mora biti 1 ili više",
        lastFight: "Barem jedna borba mora ostati u povijesti borbi",
        imageType: "Odaberite datoteku slike",
        imageSize: "Slika može biti najviše {{max}} MB",
        imageUpload: "Prijenos slike nije uspio. Pokušajte ponovno.",
        invalidUrl: "URL mora počinjati s http:// ili https://",
        logoType: "Dopuštene su samo PNG ili JPG slike",
        logoSize: "Slika može biti najviše 2 MB",
    },
};
